import React, { useEffect, useMemo, useState } from 'react'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import OrgLandingPageEditor from '../components/OrgLandingPageEditor'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

const TEMPLATE_GUIDES = {
  default: {
    featured: true,
    tags: ['기본형', '균형형', '추천'],
    headline: '조직 소개와 리크루팅을 가장 균형 있게 담는 기본형 템플릿',
    scenario: '처음 조직홈페이지를 만드는 팀, 소개/혜택/지원까지 고르게 보여주고 싶은 조직에 적합합니다.',
    operatorNote: '가장 범용적인 기본 템플릿입니다. 처음 시작점으로 쓰고 필요한 섹션만 다듬기 좋습니다.',
  },
  'template-basic-2': {
    tags: ['전환형', '정착강조', '좌측히어로'],
    headline: '정착 지원과 실행력을 빠르게 보여주는 전환형 템플릿',
    scenario: '신입 유입, 빠른 실행력, 초반 온보딩 강점을 강조하고 싶은 조직에 적합합니다.',
    operatorNote: '혜택을 초반에 배치해 지원자가 "들어오면 무엇이 좋은지"를 먼저 이해하도록 구성했습니다.',
  },
  'template-basic-3': {
    tags: ['브랜딩형', '리더강조', '신뢰형'],
    headline: '브랜드 신뢰와 리더 철학을 앞세우는 소개형 템플릿',
    scenario: '조직의 철학, 운영 원칙, 장기 성장 관점을 먼저 설득하고 싶은 조직에 적합합니다.',
    operatorNote: '대표/조직장 메시지를 앞에 배치해 브랜딩 중심의 첫인상을 만들 수 있습니다.',
  },
  'template-basic-4': {
    tags: ['영상형', '몰입형', '콘텐츠강조'],
    headline: '영상과 함께 브랜드 스토리를 몰입감 있게 전달하는 템플릿',
    scenario: '리더 인터뷰, 소개 영상, 교육 영상 등 영상 자산이 이미 있는 조직에 적합합니다.',
    operatorNote: '각 섹션에 영상 콘텐츠를 붙여 랜딩페이지 체류시간과 몰입감을 높이기 좋습니다.',
  },
  'template-basic-5': {
    tags: ['상담전환형', '리드강조', '빠른문의'],
    headline: '문의와 상담 신청을 가장 빠르게 유도하는 상담전환형 템플릿',
    scenario: '상세 브랜딩보다 빠른 리드 수집과 상담 전환이 중요한 조직에 적합합니다.',
    operatorNote: '상담 신청 폼을 상단에 배치해 최소 입력으로 바로 전환시키는 구조입니다.',
  },
}

const DEFAULT_PRESET = {
  templateId: 'default',
  meta: { title: '', description: '', keywords: '' },
  hero: { title: '조직명을 입력해주세요', subtitle: '첫 화면 소개 문구를 입력해주세요', imageFileId: '', ctaText: '', ctaLink: '' },
  about: { title: 'ABOUT', content: '소개 문구를 입력해주세요', imageFileId: '' },
  aboutItems: [],
  stats: [],
  ceo: { name: '', title: '', message: '', imageFileId: '' },
  contact: { address: '', phone: '', email: '', kakaoLink: '' },
  style: { primaryColor: '#0066CC' },
  navigationMenu: [
    { id: 'hero', label: '홈', type: 'section', sectionRef: 'hero', enabled: true, order: 0 },
    { id: 'about', label: '조직소개', type: 'section', sectionRef: 'about', enabled: true, order: 1 },
    { id: 'ceo', label: '관리자소개', type: 'section', sectionRef: 'ceo', enabled: true, order: 2 },
    { id: 'contact', label: '문의하기', type: 'section', sectionRef: 'contact', enabled: true, order: 3 }
  ],
  logoFileId: ''
}

export default function OrgHomepageTemplatesPage() {
  const { show } = useToast()
  const { allow } = useAccess()
  const [q, setQ] = useState('')
  const [items, setItems] = useState([])
  const [, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)
  const [preset, setPreset] = useState(DEFAULT_PRESET)
  const [metaModalOpen, setMetaModalOpen] = useState(false)
  const [pendingPresetToSave, setPendingPresetToSave] = useState(null)

  const canManage = allow.perm([Permissions.TEMPLATE_MANAGE])
  const getPreviewImageUrl = (fileId) => {
    if (!fileId) return ''
    const raw = String(fileId)
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) return raw
    const apiBase = (api?.defaults?.baseURL || '').replace(/\/$/, '')
    return `${apiBase}/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(fileId))}/preview`
  }

  const reload = async () => {
    setLoading(true)
    try {
      const res = await api.get('/org-landing-page-templates', { params: { q: q || undefined } })
      const payload = res?.data?.data || res?.data
      setItems(Array.isArray(payload?.items) ? payload.items : [])
    } catch {
      show('조직홈페이지 템플릿을 불러오지 못했습니다', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  const filtered = useMemo(() => items, [items])
  const selectedTemplate = useMemo(
    () => (items || []).find(t => String(t.id) === String(selectedTemplateId)),
    [items, selectedTemplateId]
  )
  const selectedGuide = useMemo(
    () => TEMPLATE_GUIDES[selectedTemplate?.code] || null,
    [selectedTemplate]
  )
  const templateChipItems = useMemo(
    () => filtered.map((item) => ({
      ...item,
      guide: TEMPLATE_GUIDES[item.code] || null,
    })),
    [filtered]
  )

  const resetForm = () => {
    setEditingId(null)
    setSelectedTemplateId('')
    setCode('')
    setName('')
    setDescription('')
    setIsActive(true)
    setSortOrder(0)
    setPreset(DEFAULT_PRESET)
  }

  const onEdit = (item) => {
    setSelectedTemplateId(String(item.id))
    setEditingId(item.id)
    setCode(item.code || '')
    setName(item.name || '')
    setDescription(item.description || '')
    setIsActive(item.isActive !== false)
    setSortOrder(Number(item.sortOrder || 0))
    setPreset(item.preset || DEFAULT_PRESET)
  }

  const saveTemplate = async (presetToSave) => {
    if (!canManage) {
      show('권한이 없습니다', 'error')
      return
    }
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    const hh = String(now.getHours()).padStart(2, '0')
    const mm = String(now.getMinutes()).padStart(2, '0')
    const ss = String(now.getSeconds()).padStart(2, '0')
    const autoSuffix = `${y}${m}${d}${hh}${mm}${ss}`
    const normalizedCode = (code || '').trim() || `template-${autoSuffix}`
    const normalizedName = (name || '').trim() || `신규 템플릿 ${y}-${m}-${d} ${hh}:${mm}`
    setSaving(true)
    try {
      const body = {
        code: normalizedCode,
        name: normalizedName,
        description,
        preset: presetToSave,
        isActive,
        sortOrder: Number(sortOrder || 0)
      }
      if (editingId) {
        await api.put(`/org-landing-page-templates/${editingId}`, body)
        show('템플릿이 수정되었습니다', 'success')
      } else {
        await api.post('/org-landing-page-templates', body)
        show('템플릿이 생성되었습니다', 'success')
        setCode(normalizedCode)
        setName(normalizedName)
      }
      resetForm()
      await reload()
    } catch (e2) {
      show('템플릿 저장 실패: ' + (e2?.response?.data?.message || e2?.message || '알 수 없는 오류'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleEditorSave = async (payload) => {
    if (editingId) {
      await saveTemplate(payload)
      return
    }
    setPendingPresetToSave(payload)
    setMetaModalOpen(true)
  }

  const onDelete = async (id) => {
    if (!canManage) return
    if (!window.confirm('정말 삭제하시겠습니까?')) return
    try {
      await api.delete(`/org-landing-page-templates/${id}`)
      show('삭제되었습니다', 'success')
      await reload()
    } catch {
      show('삭제 실패', 'error')
    }
  }

  const onClone = (item) => {
    setEditingId(null)
    setCode(`${item.code || 'template'}-copy`)
    setName(`${item.name || '템플릿'} (복제본)`)
    setDescription(item.description || '')
    setIsActive(item.isActive !== false)
    setSortOrder(Number(item.sortOrder || 0) + 1)
    setPreset(item.preset || DEFAULT_PRESET)
    show('템플릿 복제본으로 불러왔습니다. 코드/이름을 확인 후 저장하세요.', 'success')
    setMetaModalOpen(true)
  }

  const onTemplateChange = (id) => {
    setSelectedTemplateId(id)
    const item = (items || []).find(t => String(t.id) === String(id))
    if (!item) return
    onEdit(item)
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">템플릿관리 &gt; 조직홈페이지</h1>
          <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <select
          className="w-full md:w-72 rounded border px-3 py-2 text-sm"
          value={selectedTemplateId}
          onChange={(e) => onTemplateChange(e.target.value)}
        >
          <option value="">템플릿 선택</option>
          {filtered.map(t => (
            <option key={t.id} value={t.id}>
              {t.name} {TEMPLATE_GUIDES[t.code]?.tags?.[0] ? `· ${TEMPLATE_GUIDES[t.code].tags[0]}` : ''} ({t.code})
            </option>
          ))}
        </select>
        <input
          className="w-full md:w-96 rounded border px-3 py-2 text-sm"
          placeholder="검색: 코드/이름"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="rounded border px-3 py-2 text-sm" onClick={reload}>검색</button>
        <button type="button" className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100" onClick={resetForm}>새 템플릿</button>
        <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setMetaModalOpen(true)}>메타정보</button>
        {selectedTemplate && (
          <button type="button" className="rounded border px-3 py-2 text-sm text-indigo-700" onClick={() => onClone(selectedTemplate)}>복제</button>
        )}
        {selectedTemplate && (
          <button type="button" className="rounded border px-3 py-2 text-sm text-red-700" onClick={() => onDelete(selectedTemplate.id)}>삭제</button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {templateChipItems.map((item) => {
          const active = String(item.id) === String(selectedTemplateId)
          return (
            <button
              key={`template-chip-${item.id}`}
              type="button"
              onClick={() => onTemplateChange(String(item.id))}
              className={`rounded-full border px-3 py-2 text-xs transition ${
                active
                  ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="font-semibold">{item.name}</span>
              {item.guide?.tags?.length ? <span className={`ml-2 ${active ? 'text-white/75' : 'text-slate-400'}`}>{item.guide.tags[0]}</span> : null}
            </button>
          )
        })}
      </div>

      {selectedTemplate ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              {selectedTemplate.previewImageFileId ? (
                <img
                  src={getPreviewImageUrl(selectedTemplate.previewImageFileId)}
                  alt={selectedTemplate.name || '템플릿 미리보기'}
                  className="aspect-[4/3] w-full object-cover"
                />
              ) : (
                <div className="flex aspect-[4/3] items-center justify-center text-sm text-slate-400">
                  미리보기 이미지 없음
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                  {selectedTemplate.name || '템플릿'}
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                  {selectedTemplate.code}
                </span>
                {selectedGuide?.featured ? (
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                    추천 템플릿
                  </span>
                ) : null}
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${selectedTemplate.isActive !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {selectedTemplate.isActive !== false ? '활성 템플릿' : '비활성 템플릿'}
                </span>
              </div>
              {selectedGuide?.tags?.length ? (
                <div className="flex flex-wrap gap-2">
                  {selectedGuide.tags.map((tag) => (
                    <span key={`${selectedTemplate.code}-${tag}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
              <div>
                <div className="text-lg font-semibold text-slate-900">
                  {selectedGuide?.headline || selectedTemplate.description || '조직홈페이지 템플릿'}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  {selectedTemplate.description || '템플릿 설명이 아직 없습니다.'}
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">추천 사용 시나리오</div>
                  <div className="mt-2 text-sm leading-6 text-slate-700">
                    {selectedGuide?.scenario || '이 템플릿이 잘 맞는 사용 시나리오를 여기에 안내할 수 있습니다.'}
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">운영자 메모</div>
                  <div className="mt-2 text-sm leading-6 text-slate-700">
                    {selectedGuide?.operatorNote || '운영자가 템플릿 선택 시 참고할 포인트를 여기에 안내할 수 있습니다.'}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                마지막 수정 {selectedTemplate.updatedAt ? formatDateTimeMinute(selectedTemplate.updatedAt) : '-'}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="min-h-0">
        <OrgLandingPageEditor
          templateMode
          templateData={preset}
          selectedOrg={{ name: name || '템플릿 미리보기' }}
          onTemplateSave={handleEditorSave}
        />
      </div>

      {metaModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-lg bg-white border shadow-lg p-4 space-y-3">
            <h3 className="text-lg font-semibold">{editingId ? '템플릿 메타정보 수정' : '템플릿 메타정보 생성'}</h3>
            <input className="w-full rounded border px-3 py-2" placeholder="코드 (예: basic-v1)" value={code} onChange={(e) => setCode(e.target.value)} />
            <input className="w-full rounded border px-3 py-2" placeholder="템플릿명" value={name} onChange={(e) => setName(e.target.value)} />
            <textarea className="w-full rounded border px-3 py-2" rows={2} placeholder="설명" value={description} onChange={(e) => setDescription(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                활성
              </label>
              <input className="w-full rounded border px-3 py-2" type="number" placeholder="정렬순서" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2" onClick={() => setMetaModalOpen(false)}>닫기</button>
              <button
                type="button"
                disabled={!canManage || saving}
                className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-50"
                onClick={async () => {
                  const targetPreset = pendingPresetToSave || preset
                  setPendingPresetToSave(null)
                  setMetaModalOpen(false)
                  await saveTemplate(targetPreset)
                }}
              >
                {saving ? '저장 중...' : editingId ? '수정 저장' : '생성 저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

